// =============================================================================
// OperatingCostsCard — Operating costs against a booking
// =============================================================================
// Owner-only internal ledger of money Lanz spent against this booking that
// is NEITHER charged to the customer NOR paid to the chauffeur. Examples:
// concierge fees, partner gifts, parking we ate, flowers.
//
// Sits below BillingDetailsCard in the main column. Returns null for
// non-owners — dispatchers literally never see this card.
//
// Edit semantics:
//   - Inline edit on blur (description, amount, VAT, date). Mirrors the
//     BillingLineRow pattern on BillingDetailsCard.
//   - Add new line via "+ Add line" button (same shape as NewLineForm).
//   - Delete via × icon on each row.
//   - All mutations go through the dedicated booking_operating_costs table
//     via /bookings/:id/operating-costs endpoints (completely separate from
//     booking_billing_lines, so there's zero risk of leaking into customer
//     or chauffeur ledgers).
//
// Net margin row at the bottom: customer gross − chauffeur gross − operating
// costs gross. Only shown when there's enough data to be meaningful.
// =============================================================================

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Wallet, Plus, XCircle } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { Button } from '../../components/ui/Button.jsx';

export default function OperatingCostsCard({ booking }) {
    const { isOwner } = useCurrentUser();
    if (!isOwner) return null;
    return <OperatingCostsCardInner booking={booking} />;
}

function OperatingCostsCardInner({ booking }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const queryKey = ['booking', booking.id, 'operating-costs'];

    const costsQuery = useQuery({
        queryKey,
        queryFn: () => api.get(`/bookings/${booking.id}/operating-costs`),
    });

    const lines = costsQuery.data?.lines || [];

    function invalidate() {
        return queryClient.invalidateQueries({ queryKey });
    }

    // --- Mutations ---------------------------------------------------------

    const createMutation = useMutation({
        mutationFn: (payload) =>
            api.post(`/bookings/${booking.id}/operating-costs`, payload),
        onSuccess: () => {
            toast.success('Operating cost added');
            invalidate();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not add';
            toast.error(msg);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ costId, payload }) =>
            api.patch(`/bookings/${booking.id}/operating-costs/${costId}`, payload),
        onSuccess: () => {
            invalidate();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not update';
            toast.error(msg);
            invalidate(); // resync local optimism
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (costId) =>
            api.delete(`/bookings/${booking.id}/operating-costs/${costId}`),
        onSuccess: () => {
            toast.success('Line removed');
            invalidate();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not delete';
            toast.error(msg);
        },
    });

    function handleDelete(costId) {
        if (!window.confirm('Remove this operating cost?')) return;
        deleteMutation.mutate(costId);
    }

    // --- Totals ------------------------------------------------------------

    const totals = computeTotals(lines);

// Net margin = customer gross − chauffeur gross − operating costs gross.
    //
    // We subscribe to both billing-line queries via useQuery (not
    // getQueryData) so this component re-renders when those caches load
    // or refetch. Using the SAME query keys as BillingDetailsCard means
    // React Query dedupes — no extra network calls, just shared data with
    // proper subscription.
    //
    // Previous version used queryClient.getQueryData() which is a synchronous
    // snapshot read with no subscription. If the caches hadn't landed yet
    // (or had been invalidated and were refetching), the snapshot returned
    // undefined → fell through to [] → margin computed against zero. The
    // component never re-rendered when the real data arrived, so you got
    // stale figures until something else triggered a render. That was the
    // page-refresh flicker.
    const customerLinesQuery = useQuery({
        queryKey: ['booking', booking.id, 'billing-lines', 'customer'],
        queryFn: () => api.get(`/bookings/${booking.id}/billing-lines?side=customer`),
    });
    const chauffeurLinesQuery = useQuery({
        queryKey: ['booking', booking.id, 'billing-lines', 'chauffeur'],
        queryFn: () => api.get(`/bookings/${booking.id}/billing-lines?side=chauffeur`),
    });
    const customerLines = customerLinesQuery.data?.lines || [];
    const chauffeurLines = chauffeurLinesQuery.data?.lines || [];
const customerNet = sumLinesNet(customerLines);
    const chauffeurNet = sumLinesNet(chauffeurLines);
    const costsNet = totals.net;
    const netMargin = customerNet - chauffeurNet - costsNet;
    const marginPct = customerNet > 0 ? (netMargin / customerNet) * 100 : 0;

    // Show the margin row only when ALL three queries have settled. Avoids
    // briefly displaying a half-computed figure during initial load.
    const allLoaded = !customerLinesQuery.isLoading
        && !chauffeurLinesQuery.isLoading
        && !costsQuery.isLoading;
    const hasEnoughForMargin = allLoaded
        && (customerLines.length > 0 || chauffeurLines.length > 0 || lines.length > 0);

    return (
        <div className="card">
            <div className="card-header flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                        <Wallet className="h-4 w-4 text-ink-subtle" />
                        Operating costs
                    </h2>
                    <p className="text-xs text-ink-subtle mt-0.5">
                        Internal costs against this booking. Not charged to customer, not paid to chauffeur.
                    </p>
                </div>
            </div>
            <div className="card-body">
                {costsQuery.isLoading ? (
                    <div className="text-xs text-ink-subtle italic py-2">Loading…</div>
                ) : (
                    <div className="space-y-2">
                        {lines.map((line) => (
                            <OperatingCostRow
                                key={line.id}
                                line={line}
                                saving={updateMutation.isPending || deleteMutation.isPending}
                                onSave={(payload) => updateMutation.mutate({ costId: line.id, payload })}
                                onDelete={() => handleDelete(line.id)}
                            />
                        ))}

                        <NewOperatingCostForm
                            saving={createMutation.isPending}
                            onCreate={(payload) => createMutation.mutate(payload)}
                        />

                        {lines.length === 0 && (
                            <p className="text-xs text-ink-subtle italic py-1">
                                No operating costs recorded for this booking.
                            </p>
                        )}
                    </div>
                )}

                {/* Totals strip */}
                {lines.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-paper-border-soft space-y-1 text-xs">
                        <div className="flex justify-between text-ink-subtle">
                            <span>Net</span>
                            <span>£{totals.net.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-ink-subtle">
                            <span>VAT</span>
                            <span>£{totals.vat.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-ink font-semibold text-sm pt-1">
                            <span>Gross</span>
                            <span>£{totals.gross.toFixed(2)}</span>
                        </div>
                    </div>
                )}

           {/* Net margin row — all figures NET (ex VAT) to match the
                    bookings list TotalsBar and stats page. Single source of
                    truth across the three surfaces. */}
                {hasEnoughForMargin && (
                    <div className="mt-4 rounded-md bg-canvas/60 px-4 py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium">
                                Net margin
                                <span className="text-[10px] text-ink-subtle ml-1 font-normal normal-case tracking-normal">
                                    ex VAT
                                </span>
                            </div>
                            <div className="text-[11px] text-ink-subtle mt-0.5 truncate">
                                Customer £{customerNet.toFixed(2)} − Chauffeur £{chauffeurNet.toFixed(2)} − Costs £{costsNet.toFixed(2)}
                            </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <div className={`text-xl font-semibold ${netMargin >= 0 ? 'text-ink' : 'text-red-700'}`}>
                                £{netMargin.toFixed(2)}
                            </div>
                            {customerNet > 0 && (
                                <div className="text-xs text-ink-subtle">
                                    {marginPct.toFixed(1)}%
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// One row — inline-editable on blur
// -----------------------------------------------------------------------------
function OperatingCostRow({ line, saving, onSave, onDelete }) {
    const [description, setDescription] = useState(line.description);
    const [amount, setAmount] = useState(String(line.amount_ex_vat ?? ''));
    const [vatRate, setVatRate] = useState(Number(line.vat_rate) || 0);
    const [incurredDate, setIncurredDate] = useState(
        line.incurred_date ? String(line.incurred_date).slice(0, 10) : ''
    );

    // Re-sync from props when the line changes (after a successful refetch).
    useEffect(() => {
        setDescription(line.description);
        setAmount(String(line.amount_ex_vat ?? ''));
        setVatRate(Number(line.vat_rate) || 0);
        setIncurredDate(line.incurred_date ? String(line.incurred_date).slice(0, 10) : '');
    }, [line.id, line.description, line.amount_ex_vat, line.vat_rate, line.incurred_date]);

    function handleDescriptionBlur() {
        const next = description.trim();
        if (next === line.description) return;
        if (next.length === 0) {
            setDescription(line.description);
            toast.error('Description cannot be empty');
            return;
        }
        onSave({ description: next });
    }

    function handleAmountBlur() {
        const parsed = parseFloat(amount);
        if (!Number.isFinite(parsed) || parsed < 0) {
            setAmount(String(line.amount_ex_vat ?? ''));
            toast.error('Amount must be a non-negative number');
            return;
        }
        if (parsed === Number(line.amount_ex_vat)) return;
        onSave({ amount_ex_vat: parsed });
    }

    function handleVatChange(e) {
        const next = parseFloat(e.target.value);
        setVatRate(next);
        if (next === Number(line.vat_rate)) return;
        onSave({ vat_rate: next });
    }

    function handleDateBlur() {
        if (!incurredDate) {
            setIncurredDate(String(line.incurred_date).slice(0, 10));
            return;
        }
        if (incurredDate === String(line.incurred_date).slice(0, 10)) return;
        onSave({ incurred_date: incurredDate });
    }

    return (
        <div className="flex items-center gap-1.5">
            <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={handleDescriptionBlur}
                disabled={saving}
                placeholder="Description"
                className="flex-1 min-w-0 text-sm border border-paper-border rounded px-2 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40 disabled:cursor-not-allowed"
            />
            <input
                type="date"
                value={incurredDate}
                onChange={(e) => setIncurredDate(e.target.value)}
                onBlur={handleDateBlur}
                disabled={saving}
                className="text-xs border border-paper-border rounded px-1.5 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40 disabled:cursor-not-allowed"
            />
            <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setAmount(v);
                }}
                onBlur={handleAmountBlur}
                disabled={saving}
                placeholder="0.00"
                className="w-20 text-right text-sm border border-paper-border rounded px-2 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40 disabled:cursor-not-allowed"
            />
            <select
                value={vatRate}
                onChange={handleVatChange}
                disabled={saving}
                className="text-xs border border-paper-border rounded px-1.5 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40 disabled:cursor-not-allowed"
            >
                <option value={0}>0%</option>
                <option value={0.2}>20%</option>
            </select>
            <button
                type="button"
                onClick={onDelete}
                disabled={saving}
                className="text-ink-subtle hover:text-red-700 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Remove cost"
            >
                <XCircle className="h-4 w-4" />
            </button>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Inline form for a new operating cost line
// -----------------------------------------------------------------------------
function NewOperatingCostForm({ saving, onCreate }) {
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [vatRate, setVatRate] = useState(0);
    const [incurredDate, setIncurredDate] = useState(toIsoDate(new Date()));
    const [adding, setAdding] = useState(false);

    function resetForm() {
        setDescription('');
        setAmount('');
        setVatRate(0);
        setIncurredDate(toIsoDate(new Date()));
        setAdding(false);
    }

    function tryCreate() {
        const trimmed = description.trim();
        const parsed = parseFloat(amount);
        if (trimmed.length === 0) {
            toast.error('Description required');
            return;
        }
        if (!Number.isFinite(parsed) || parsed < 0) {
            toast.error('Amount required');
            return;
        }
        onCreate({
            description: trimmed,
            amount_ex_vat: parsed,
            vat_rate: vatRate,
            incurred_date: incurredDate,
        });
        resetForm();
    }

    if (!adding) {
        return (
            <button
                type="button"
                onClick={() => setAdding(true)}
                className="text-xs text-ink-info hover:underline inline-flex items-center gap-1 mt-1"
            >
                <Plus className="h-3 w-3" />
                Add operating cost
            </button>
        );
    }

    return (
        <div className="flex items-center gap-1.5 pt-1">
            <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={saving}
                placeholder="e.g. Concierge fee, flowers, parking"
                autoFocus
                className="flex-1 min-w-0 text-sm border border-paper-border rounded px-2 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
            />
            <input
                type="date"
                value={incurredDate}
                onChange={(e) => setIncurredDate(e.target.value)}
                disabled={saving}
                className="text-xs border border-paper-border rounded px-1.5 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
            />
            <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setAmount(v);
                }}
                disabled={saving}
                placeholder="0.00"
                className="w-20 text-right text-sm border border-paper-border rounded px-2 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
            />
            <select
                value={vatRate}
                onChange={(e) => setVatRate(parseFloat(e.target.value))}
                disabled={saving}
                className="text-xs border border-paper-border rounded px-1.5 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
            >
                <option value={0}>0%</option>
                <option value={0.2}>20%</option>
            </select>
            <button
                type="button"
                onClick={tryCreate}
                disabled={saving}
                className="text-xs text-ink-info hover:underline px-1 disabled:opacity-50"
                title="Save line"
            >
                Save
            </button>
            <button
                type="button"
                onClick={resetForm}
                disabled={saving}
                className="text-ink-subtle hover:text-ink flex-shrink-0"
                title="Cancel"
            >
                <XCircle className="h-4 w-4" />
            </button>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
function computeTotals(lines) {
    let net = 0;
    let vat = 0;
    for (const line of lines) {
        const amt = Number(line.amount_ex_vat) || 0;
        const rate = Number(line.vat_rate) || 0;
        net += amt;
        vat += amt * rate;
    }
    return { net, vat, gross: net + vat };
}

// Sum lines NET (ex VAT). Matches the bookings-list TotalsBar convention
// and the stats page. Net margin = customer net − chauffeur net − costs net.
// Renamed from sumLinesGross — was the source of the booking-detail page
// showing different figures than the list page.
function sumLinesNet(lines) {
    let total = 0;
    for (const line of lines) {
        total += Number(line.amount_ex_vat) || 0;
    }
    return total;
}

function toIsoDate(d) {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${da}`;
}
